import type { ContactExpandOpts, IContactPerson, IContactPersonRelation, IFetchContactParams, MediaFileExpandOpts } from '@condo/domain';
import { ContactType } from '@condo/domain';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
    assignContact,
    type IAssignContactRelation,
    getContacts,
    getMfhContacts,
    getOneContact,
    mergeContacts,
    removeContactAssignment,
    saveContact,
    syncSlackAvatar,
} from '../api/contacts.api';
import { uploadContactAvatar, type UploadContactAvatarParams } from '../lib/contactAvatarUpload';
import { formatContactPersonLabel } from '../lib/contactHelpers';
import { CURRENT_USER_QUERY_KEY } from '@/entities/user/config/user-query-keys';
import { useUsers } from '@/entities/user';
import { withGlobalErrorHandling } from '@/shared/lib/query-client';

const CONTACT_STALE_TIME = 5 * 60 * 1000;
const CONTACT_GC_TIME = 10 * 60 * 1000;
const BOOTSTRAP_QUERY_KEY = ['bootstrap-data'] as const;

export const contactQueryKeys = {
    all: ['contacts'] as const,
    details: () => [...contactQueryKeys.all, 'detail'] as const,
    detail: (id: number | 'me', expandKey?: string) => [...contactQueryKeys.details(), id, ...(expandKey ? [expandKey] : [])] as const,
    byEstate: (estateId: number) => [...contactQueryKeys.all, 'estate', estateId] as const,
    byMfh: (mfhId: number) => [...contactQueryKeys.all, 'mfh', mfhId] as const,
    search: (query: string) => [...contactQueryKeys.all, 'search', query] as const,
    list: (params: IFetchContactParams) => [...contactQueryKeys.all, 'list', params] as const,
    byIds: (ids: number[]) => [...contactQueryKeys.all, 'by-ids', ids] as const,
};

export function invalidateContactAvatarCaches(queryClient: QueryClient, contactId?: number) {
    if (contactId != null) {
        queryClient.invalidateQueries({ queryKey: contactQueryKeys.detail(contactId) });
    }
    queryClient.invalidateQueries({ queryKey: contactQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: BOOTSTRAP_QUERY_KEY });
}

export const useSaveContact = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ contact, relation }: { contact: Partial<IContactPerson>; relation?: Partial<IContactPersonRelation> }) => saveContact(contact, relation),
        onSuccess: (savedContact: unknown, variables) => {
            const contact = savedContact as { contactId?: number };
            if (contact.contactId) {
                queryClient.invalidateQueries({ queryKey: contactQueryKeys.detail(contact.contactId) });
            }
            if (variables.relation?.mfhId) {
                queryClient.invalidateQueries({ queryKey: contactQueryKeys.byMfh(variables.relation.mfhId) });
                queryClient.invalidateQueries({ queryKey: ['assets', 'detail', variables.relation.mfhId] });
            }
            queryClient.invalidateQueries({ queryKey: contactQueryKeys.all });
        },
        ...withGlobalErrorHandling(),
    });
};

export const useUploadContactAvatar = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (params: UploadContactAvatarParams) => uploadContactAvatar(params),
        onSuccess: (_mediaFile, variables) => {
            invalidateContactAvatarCaches(queryClient, variables.contact.contactId);
        },
        ...withGlobalErrorHandling(),
    });
};

export const useSyncSlackAvatar = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (contactId: number) => syncSlackAvatar(contactId),
        onSuccess: updatedContact => {
            invalidateContactAvatarCaches(queryClient, updatedContact.contactId);
        },
        ...withGlobalErrorHandling(),
    });
};

export const useAssignContact = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: { relation: IAssignContactRelation }) => assignContact(data),
        onSuccess: (_result, variables) => {
            queryClient.invalidateQueries({ queryKey: contactQueryKeys.detail(variables.relation.contactId) });
            queryClient.invalidateQueries({ queryKey: contactQueryKeys.all });

            if (variables.relation.estateId) {
                queryClient.invalidateQueries({ queryKey: ['estates', 'detail', variables.relation.estateId] });
            }
            if (variables.relation.mfhId) {
                queryClient.invalidateQueries({ queryKey: ['assets', 'detail', variables.relation.mfhId] });
                queryClient.invalidateQueries({ queryKey: contactQueryKeys.byMfh(variables.relation.mfhId) });
            }
            if (variables.relation.mfhPortfolioId) {
                queryClient.invalidateQueries({ queryKey: ['mfhPortfolios', 'detail', variables.relation.mfhPortfolioId] });
            }
        },
        ...withGlobalErrorHandling(),
    });
};

export const useRemoveContactAssignment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ contactRelationId }: { contactRelationId: number; mfhPortfolioId?: number }) => {
            return removeContactAssignment(contactRelationId);
        },
        onSuccess: (_result, variables) => {
            queryClient.invalidateQueries({ queryKey: contactQueryKeys.all });
            queryClient.invalidateQueries({ queryKey: ['assets', 'detail'] }); // mirrors assetQueryKeys.details()

            if (variables.mfhPortfolioId) {
                queryClient.invalidateQueries({ queryKey: ['mfhPortfolios', 'detail', variables.mfhPortfolioId] });
            }
        },
        ...withGlobalErrorHandling(),
    });
};

export const useMergeContacts = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ contact, contacts }: { contact: IContactPerson; contacts: IContactPerson[] }) => mergeContacts(contact, contacts),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: contactQueryKeys.all });
        },
        ...withGlobalErrorHandling(),
    });
};

export const useEstateContacts = (estateId?: number) => {
    return useQuery({
        queryKey: contactQueryKeys.byEstate(estateId!),
        queryFn: async (): Promise<IContactPerson[]> => {
            const response = await getContacts({ query: { estateId } });
            return (response?.contacts ?? []).filter((contact: IContactPerson) => contact.email);
        },
        enabled: !!estateId,
        staleTime: CONTACT_STALE_TIME,
        gcTime: CONTACT_GC_TIME,
        ...withGlobalErrorHandling(),
    });
};

export const useEstateBuyerContacts = (estateId?: number, enabled = true) => {
    return useQuery({
        queryKey: [...contactQueryKeys.byEstate(estateId!), 'buyers'],
        queryFn: async (): Promise<IContactPerson[]> => {
            const response = await getContacts({
                query: { estateId, contactType: [ContactType.Buyer] },
                expand: {
                    select: ['contactId', 'firstname', 'lastname', 'fullname', 'salutation', 'contactType'],
                },
            });
            return response?.contacts ?? [];
        },
        enabled: enabled && !!estateId,
        staleTime: CONTACT_STALE_TIME,
        gcTime: CONTACT_GC_TIME,
        ...withGlobalErrorHandling(),
    });
};

/**
 * Scalar contact fields only — same shape as cockpit `ContactQuickSearch`.
 * Joining avatar variants on a paginated search drops `email`, so pickers that hide
 * contacts without an email render an empty list.
 */
const CONTACT_PICKER_SEARCH_EXPAND: ContactExpandOpts = {
    select: ['contactId', 'email', 'firstname', 'lastname', 'fullname', 'salutation', 'contactType', 'userId'],
};

const CONTACT_PICKER_SEARCH_EXPAND_WITH_LOCATION: ContactExpandOpts = {
    ...CONTACT_PICKER_SEARCH_EXPAND,
    expand: {
        location: {
            select: ['locationId', 'street', 'zipcode', 'city', 'houseNumber'],
        },
    },
};

export interface SearchContactsOpts {
    /** Include `location` for address columns. Omit on email pickers — the join can drop `email`. */
    withLocation?: boolean;
}

export const useExternalContactSearch = (searchQuery: string, enabled = true) => {
    const trimmed = searchQuery.trim();
    return useQuery({
        queryKey: [...contactQueryKeys.search(trimmed), 'picker'] as const,
        queryFn: async (): Promise<IContactPerson[]> => {
            const response = await getContacts({
                query: { query: trimmed, page: 1, size: 20 },
                expand: CONTACT_PICKER_SEARCH_EXPAND,
            });
            return (response?.contacts ?? []).filter((contact: IContactPerson) => contact.email);
        },
        enabled: enabled && trimmed.length >= 2,
        staleTime: CONTACT_STALE_TIME,
        gcTime: CONTACT_GC_TIME,
        ...withGlobalErrorHandling(),
    });
};

/**
 * Fetch a single contact by ID
 * @param contactId - Contact ID to fetch
 * @param expandOpts - Optional expand options for nested data (employment, accounting, etc.)
 */
export const useOneContact = (contactId: number | undefined, expandOpts?: ContactExpandOpts) => {
    const expandKey = expandOpts ? JSON.stringify(expandOpts) : undefined;
    return useQuery({
        queryKey: contactQueryKeys.detail(contactId!, expandKey),
        queryFn: async () => {
            const response = await getOneContact(contactId!, expandOpts);
            return response.contact;
        },
        enabled: !!contactId,
        staleTime: CONTACT_STALE_TIME,
        ...withGlobalErrorHandling(),
    });
};

const MY_CONTACT_RELATION_EXPAND: ContactExpandOpts = {
    expand: {
        contactRelation: {
            select: ['contactRelationId', 'companyFunction', 'mfhId'],
        },
    },
};

/**
 * Current authenticated contact with `contactRelation` expand — used for Cockpit-parity
 * Asset Manager fallback gates on envelope approval actions.
 */
export function useMyContactRelations(enabled = true) {
    const expandKey = JSON.stringify(MY_CONTACT_RELATION_EXPAND);
    return useQuery({
        queryKey: contactQueryKeys.detail('me', expandKey),
        queryFn: async () => {
            const response = await getOneContact('me', MY_CONTACT_RELATION_EXPAND);
            return response.contact;
        },
        enabled,
        staleTime: CONTACT_STALE_TIME,
        ...withGlobalErrorHandling(),
    });
}

export const useContacts = (params: IFetchContactParams) => {
    return useQuery({
        queryKey: contactQueryKeys.list(params),
        queryFn: async () => {
            const response = await getContacts(params);
            return { contacts: response.contacts ?? [], total: response.total ?? 0 };
        },
        staleTime: 10 * 60 * 1000,
        gcTime: 15 * 60 * 1000,
        ...withGlobalErrorHandling(),
    });
};

const AVATAR_MEDIA_FILE_EXPAND: MediaFileExpandOpts = {
    select: ['path', 'mediaFileId'],
    expand: {
        variants: { select: ['path', 'variant'] },
    },
};

export const useSearchContacts = (searchQuery: string, contactTypes: ContactType[], enabled = true, opts?: SearchContactsOpts) => {
    const trimmed = searchQuery.trim();
    const queryParams: IFetchContactParams = {
        query: {
            ...(trimmed ? { query: trimmed } : {}),
            ...(contactTypes.length > 0 ? { contactType: contactTypes } : {}),
            size: 20,
            page: 1,
        },
        expand: opts?.withLocation ? CONTACT_PICKER_SEARCH_EXPAND_WITH_LOCATION : CONTACT_PICKER_SEARCH_EXPAND,
    };

    return useQuery({
        queryKey: contactQueryKeys.list(queryParams),
        queryFn: async () => {
            const response = await getContacts(queryParams);
            return (response?.contacts ?? []).filter((c: IContactPerson) => c.email || c.firstname || c.lastname);
        },
        enabled,
        staleTime: CONTACT_STALE_TIME,
        gcTime: CONTACT_GC_TIME,
        ...withGlobalErrorHandling(),
    });
};

/** Enough fields to render ContactSelector chips (name / email), not just `contactId`. */
const DEFAULT_CONTACTS_BY_IDS_EXPAND: ContactExpandOpts = {
    select: ['contactId', 'firstname', 'lastname', 'fullname', 'salutation', 'contactType', 'email', 'userId'],
    expand: {
        avatarMediaFile: AVATAR_MEDIA_FILE_EXPAND,
    },
};

export const useContactsByIds = (contactIds: number[] | undefined, enabled = true, expandOpts?: ContactExpandOpts) => {
    const resolvedExpand = expandOpts ?? DEFAULT_CONTACTS_BY_IDS_EXPAND;
    /** Always key on expand so default shape changes (e.g. name `select`) invalidate stale sparse rows. */
    const expandKey = JSON.stringify(resolvedExpand);
    return useQuery({
        queryKey: [...contactQueryKeys.byIds(contactIds ?? []), expandKey] as const,
        queryFn: async (): Promise<IContactPerson[]> => {
            const response = await getContacts({
                query: { contactId: contactIds },
                expand: resolvedExpand,
            });
            return response?.contacts ?? [];
        },
        enabled: enabled && !!contactIds?.length,
        staleTime: CONTACT_STALE_TIME,
        gcTime: CONTACT_GC_TIME,
        ...withGlobalErrorHandling(),
    });
};

/** Resolves a contact id to a display label (bootstrap user name, then contact API with email fallback). */
export function useContactDisplayLabel(contactId: number | string | undefined | null): { label: string | null; isLoading: boolean } {
    const { getUserName, isLoading: usersLoading } = useUsers();
    const numericId = contactId != null && contactId !== '' && !Number.isNaN(Number(contactId)) ? Number(contactId) : undefined;

    const bootstrapLabel = numericId != null && !usersLoading ? getUserName(numericId, '', false, true, false) : '';
    const hasBootstrapLabel = Boolean(bootstrapLabel && bootstrapLabel !== '(unknown)');

    const { data: contact, isLoading: contactLoading } = useQuery({
        queryKey: contactQueryKeys.detail(numericId!),
        queryFn: async () => {
            const response = await getOneContact(numericId!);
            return response.contact ?? null;
        },
        enabled: numericId != null && !usersLoading && !hasBootstrapLabel,
        staleTime: CONTACT_STALE_TIME,
        gcTime: CONTACT_GC_TIME,
        ...withGlobalErrorHandling(),
    });

    if (numericId == null) {
        return { label: null, isLoading: false };
    }
    if (usersLoading) {
        return { label: null, isLoading: true };
    }
    if (hasBootstrapLabel) {
        return { label: bootstrapLabel, isLoading: false };
    }
    if (contactLoading) {
        return { label: null, isLoading: true };
    }
    const fromContact = contact ? formatContactPersonLabel(contact) : '';
    return { label: fromContact || String(numericId), isLoading: false };
}

export interface UseMfhContactsOptions {
    enabled?: boolean;
}

export const useMfhContacts = (mfhId: number | undefined, options?: UseMfhContactsOptions) => {
    return useQuery({
        queryKey: contactQueryKeys.byMfh(mfhId!),
        queryFn: async (): Promise<IContactPerson[]> => {
            return getMfhContacts(mfhId!);
        },
        enabled: !!mfhId && (options?.enabled ?? true),
        staleTime: CONTACT_STALE_TIME,
        gcTime: CONTACT_GC_TIME,
        ...withGlobalErrorHandling(),
    });
};
